import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Image from '../components/image/Image';
import Table from '../components/table/table';
import Input from '../components/input/Input';
import Button from '../components/button/Button';
import Dropdown from '../components/dropdown/DropDown';
import DatePicker from '../components/datepicker/DatePicker';
import Spinner from '../components/Spinner/spinner';
import Icon from '../components/icon/Icon';
import Tooltip from '../components/tooltip/Tooltip';
import { ITableData } from '../types/interfaces';
import { getOverduePageData, normalizeCuitForSearch } from '../utils/Utils';
import { getOverdueColumns, OverdueData } from '../utils/overdueColumns';
import { useFiscalIdConfig } from '../hooks/useFiscalIdConfig';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import {
  exportOverdueCsv,
  getOverdue,
  getOverdueFilters,
  getOverdueTotals,
  OverdueTotals,
  triggerCollectedOkCron,
  triggerOverdueCron,
} from '../services/GuaranteesService';
import OverdueStatusModal from '../components/modals/OverdueStatusModal';
import Modal from '../components/modal/modal';
import { routes } from '../routes/routePaths';
import PageBreadcrumb from '../components/PageBreadcrumb/PageBreadcrumb';
import { useConfig } from '../context/ConfigContext';
import { resolveLocalCurrencyCode } from '../utils/quotationAssets';
import { useToast } from '../components/toast/toast';
import {
  getDefaultOverdueFilters,
  isOverdueDateRangeValid,
  resolveCsvFilename,
  nextOverdueSortConfig,
} from '../utils/overdueFilters';
import theme from '../styles/theme';

interface FilterOption {
  id: string | number;
  name: string;
}

interface OverdueFilters {
  creditors: FilterOption[];
  types: FilterOption[];
}

const Overdue = () => {
  const fiscalId = useFiscalIdConfig();
  const localCurrencyCode = resolveLocalCurrencyCode(useConfig());
  const defaults = getDefaultOverdueFilters();
  const [overdueData, setOverdueData] = useState<OverdueData[]>([]);
  const [tableData, setTableData] = useState<ITableData>({
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0,
  });
  const [page, setPage] = useState<number>(0);
  const [fromDate, setFromDate] = useState<string>(defaults.fromDate);
  const [toDate, setToDate] = useState<string>(defaults.toDate);
  const [badgeFilter, setBadgeFilter] = useState<string>(defaults.badgeFilter);
  const [selectedCreditor, setSelectedCreditor] = useState<string>(defaults.selectedCreditor);
  const [selectedType, setSelectedType] = useState<string>(defaults.selectedType);
  const [globalIdContains, setGlobalIdContains] = useState<string>(defaults.globalIdContains);
  const [searchText, setSearchText] = useState<string>(defaults.searchText);
  const [sortConfig, setSortConfig] = useState<{ field: string | null; direction: 'asc' | 'desc' | null }>({
    field: null,
    direction: null,
  });
  const [dateError, setDateError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [filters, setFilters] = useState<OverdueFilters>({
    creditors: [],
    types: [],
  });
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedOverdueItem, setSelectedOverdueItem] = useState<OverdueData | null>(null);
  const [totals, setTotals] = useState<OverdueTotals | null>(null);
  const [isTriggeringOverdue, setIsTriggeringOverdue] = useState(false);
  const [isTriggeringCollectedOk, setIsTriggeringCollectedOk] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultModalTitle, setResultModalTitle] = useState('');
  const [resultModalMessage, setResultModalMessage] = useState('');

  const lastRequest = useRef(0);
  const ignoreSortEchoRef = useRef(false);
  const textSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialLoadDone = useRef(false);

  useEffect(() => {
    loadFilters();
    loadTotals();
  }, []);

  useEffect(() => {
    if (!isOverdueDateRangeValid(fromDate, toDate)) {
      setDateError('La fecha Hasta debe ser mayor o igual a Desde');
      return;
    }
    setDateError('');
    fetchOverdueData();
    // eslint-disable-next-line
  }, [page, fromDate, toDate, badgeFilter, selectedCreditor, selectedType, sortConfig.field, sortConfig.direction]);

  useEffect(() => {
    if (textSearchTimeoutRef.current) {
      clearTimeout(textSearchTimeoutRef.current);
    }
    textSearchTimeoutRef.current = setTimeout(() => {
      setPage(0);
      if (!initialLoadDone.current) {
        initialLoadDone.current = true;
      }
      if (!isOverdueDateRangeValid(fromDate, toDate)) return;
      fetchOverdueData();
    }, 500);

    return () => {
      if (textSearchTimeoutRef.current) {
        clearTimeout(textSearchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line
  }, [searchText, globalIdContains]);

  const loadFilters = async () => {
    try {
      const response = await getOverdueFilters();
      if (response && 'data' in response && response.data) {
        setFilters({
          creditors: response.data.creditors || [],
          types: response.data.types || [],
        });
      }
    } catch {
      // ignore filter load errors
    }
  };

  const loadTotals = async () => {
    try {
      const response = await getOverdueTotals();
      if (response) {
        setTotals(response);
      }
    } catch {
      // ignore totals load errors
    }
  };

  const formatCurrency = (value: number, currency: string): string => {
    const formatted = value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return currency === 'USD' ? `U$S ${formatted}` : `$ ${formatted}`;
  };

  const buildQueryParams = () => ({
    page,
    size: 10,
    fromDate,
    toDate,
    badge: badgeFilter || undefined,
    creditor: selectedCreditor,
    type: selectedType,
    filterText: normalizeCuitForSearch(searchText),
    globalIdContains,
    sortBy: sortConfig.field || undefined,
    sortDirection: sortConfig.direction || undefined,
  });

  const fetchOverdueData = async () => {
    if (!isOverdueDateRangeValid(fromDate, toDate)) {
      return;
    }

    const requestId = ++lastRequest.current;
    setIsLoading(true);

    try {
      const params = buildQueryParams();
      const response = await getOverdue(
        params.page ?? 0,
        params.size ?? 10,
        params.fromDate,
        params.toDate,
        params.badge,
        params.creditor,
        params.type,
        params.filterText,
        params.globalIdContains,
        params.sortBy,
        params.sortDirection,
      );

      if (lastRequest.current !== requestId) return;

      if (response && response.success === false) {
        useToast.error({
          title: response.message || 'Error al cargar vencimientos',
          id: 'overdue-fetch',
          autoClose: 5000,
        });
        return;
      }

      const pageData = getOverduePageData(response);
      if (!pageData) return;

      setTableData({
        size: pageData.size,
        page: Math.max(0, pageData.page - 1),
        totalElements: pageData.total_elements,
        totalPages: pageData.total_pages,
      });
      setOverdueData(
        pageData.content.map((row: OverdueData) => ({
          ...row,
          row_key: `${row.type}-${row.id}-${row.installment_id ?? ''}`,
        }))
      );
    } catch (error) {
      console.error('Error fetching overdue data:', error);
      if (lastRequest.current === requestId) {
        useToast.error({
          title: 'Error al cargar vencimientos',
          id: 'overdue-fetch',
          autoClose: 5000,
        });
      }
    } finally {
      if (lastRequest.current === requestId) {
        setIsLoading(false);
      }
    }
  };

  const handleChangePage = (data: number) => {
    if (data < 0 || data >= tableData.totalPages) {
      return;
    }
    setPage(data);
  };

  const sortConfigRef = useRef(sortConfig);
  sortConfigRef.current = sortConfig;

  // The table flips its own direction on every click regardless of column, so it drifts from ours
  // after a column switch; toggle from our state only. The ref guards against a double fire.
  const handleSort = (field: string) => {
    if (ignoreSortEchoRef.current) return;

    const config = nextOverdueSortConfig(sortConfigRef.current, field);

    ignoreSortEchoRef.current = true;
    setSortConfig(config);
    setPage(0);
    window.setTimeout(() => {
      ignoreSortEchoRef.current = false;
    }, 0);
  };

  const handleOpenModal = useCallback((row: OverdueData) => {
    setSelectedOverdueItem(row);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOverdueItem(null);
  };

  const handleModalSuccess = () => {
    fetchOverdueData();
    loadTotals();
  };

  const handleFromDateChange = (date: string) => {
    setFromDate(date);
    setPage(0);
  };

  const handleToDateChange = (date: string) => {
    setToDate(date);
    setPage(0);
  };

  const badgeFilterOptions: { label: string; value: string }[] = [
    { label: 'Todos los estados', value: '' },
    { label: 'Vencida', value: 'VENCIDA' },
    { label: 'Afrontada', value: 'AFRONTADA' },
    { label: 'Honrada parcial', value: 'HONRADA_PARCIAL' },
    { label: 'Honrada', value: 'HONRADA' },
    { label: 'Incobrable', value: 'INCOBRABLE' },
    { label: 'Cobrada OK', value: 'COBRADA_OK' },
    { label: 'Pendiente', value: 'PENDIENTE' },
    { label: 'Cancelado antic.', value: 'CANCELADO_ANTICIPADAMENTE' },
  ];

  const handleBadgeFilterChange = (value: string) => {
    const selected = badgeFilterOptions.find((o) => o.label === value);
    setBadgeFilter(selected?.value ?? '');
    setPage(0);
  };

  const handleCreditorChange = (value: string) => {
    if (value === 'Todos los acreedores') {
      setSelectedCreditor('');
    } else {
      const selectedFilter = filters.creditors.find((c) => c.name === value);
      setSelectedCreditor(selectedFilter ? String(selectedFilter.id) : '');
    }
    setPage(0);
  };

  const handleTypeChange = (value: string) => {
    if (value === 'Todos los instrumentos') {
      setSelectedType('');
    } else {
      const selectedFilter = filters.types.find((t) => t.name === value);
      setSelectedType(selectedFilter ? String(selectedFilter.id) : '');
    }
    setPage(0);
  };

  const isDirtyFromDefaults = () => {
    const currentDefaults = getDefaultOverdueFilters();
    return (
      fromDate !== currentDefaults.fromDate ||
      toDate !== currentDefaults.toDate ||
      badgeFilter !== currentDefaults.badgeFilter ||
      selectedCreditor !== currentDefaults.selectedCreditor ||
      selectedType !== currentDefaults.selectedType ||
      globalIdContains !== currentDefaults.globalIdContains ||
      searchText !== currentDefaults.searchText ||
      sortConfig.field !== null
    );
  };

  const handleClearFilters = () => {
    const nextDefaults = getDefaultOverdueFilters();
    setFromDate(nextDefaults.fromDate);
    setToDate(nextDefaults.toDate);
    setBadgeFilter(nextDefaults.badgeFilter);
    setSelectedCreditor(nextDefaults.selectedCreditor);
    setSelectedType(nextDefaults.selectedType);
    setGlobalIdContains(nextDefaults.globalIdContains);
    setSearchText(nextDefaults.searchText);
    setSortConfig({ field: null, direction: null });
    setDateError('');
    setPage(0);
  };

  const handleExportCsv = async () => {
    if (!isOverdueDateRangeValid(fromDate, toDate)) {
      useToast.error({ title: dateError || 'Rango de fechas inválido', id: 'overdue-export', autoClose: 5000 });
      return;
    }

    setIsExporting(true);
    try {
      const result = await exportOverdueCsv(buildQueryParams());
      if (result && 'data' in result) {
        const blob = new Blob([result.data], { type: 'text/csv;charset=utf-8' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = resolveCsvFilename(result.headers?.['content-disposition']);
        link.click();
        window.URL.revokeObjectURL(downloadUrl);
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number; data?: { message?: string } } };
      const message =
        axiosError?.response?.data?.message ||
        'No hay vencimientos para exportar con los filtros seleccionados.';
      useToast.error({ title: message, id: 'overdue-export', autoClose: 5000 });
    }
    setIsExporting(false);
  };

  const handleTriggerOverdue = async () => {
    setIsTriggeringOverdue(true);
    try {
      const response = await triggerOverdueCron();
      const updated = response?.data?.updated ?? 0;
      setResultModalTitle('Proceso finalizado');
      setResultModalMessage(`Se actualizaron ${updated} vencimientos.`);
    } catch {
      setResultModalTitle('Error en el proceso');
      setResultModalMessage('No se pudieron actualizar los vencimientos. Intente nuevamente.');
    }
    setIsTriggeringOverdue(false);
    setShowResultModal(true);
  };

  const handleTriggerCollectedOk = async () => {
    setIsTriggeringCollectedOk(true);
    try {
      const response = await triggerCollectedOkCron();
      const totalMarked = response?.data?.total_marked ?? 0;
      setResultModalTitle('Proceso finalizado');
      setResultModalMessage(`Se marcaron ${totalMarked} instrumentos como cobradas OK.`);
    } catch {
      setResultModalTitle('Error en el proceso');
      setResultModalMessage('No se pudieron actualizar las cobradas. Intente nuevamente.');
    }
    setIsTriggeringCollectedOk(false);
    setShowResultModal(true);
  };

  const handleCloseResultModal = () => {
    setShowResultModal(false);
    fetchOverdueData();
    loadTotals();
  };

  const parseDatePickerValue = (date: Date | string | null): string => {
    if (!date) return '';
    if (typeof date === 'string') {
      const parts = date.split('-');
      return `${parts[0]}-${parts[1]}-${parts[2] || '01'}`;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const columns = useMemo(
    () =>
      getOverdueColumns({
        handleOpenModal,
        country: fiscalId.country,
        sortConfig,
      }),
    [handleOpenModal, fiscalId.country, sortConfig],
  );

  const isClearFiltersDirty = isDirtyFromDefaults();
  const isGlobalIdFilterActive = globalIdContains.trim() !== '';
  const isSearchFilterActive = searchText.trim() !== '';
  const isFromDateActive = fromDate !== '';
  const isToDateActive = toDate !== '';
  const isBadgeFilterActive = badgeFilter !== '';
  const isCreditorFilterActive = selectedCreditor !== '';
  const isTypeFilterActive = selectedType !== '';
  const activeFilterBorder = theme.colors.primary.border.regular;

  return (
    <>
      <div style={{ width: '85vw' }}>
        <PageBreadcrumb items={[{ label: 'Inicio', to: routes.dashboard }, { label: 'Vencimientos' }]} />
      </div>

      <Card
        variant='neutral'
        modifier='inverted'
        spacing={'4'}
        height='-webkit-fill-available'
        width='85vw'
        style={{
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <style>{`
          .date-filter-input input {
            color: #374151 !important;
          }
          .date-filter-input input::placeholder {
            color: #6B7280 !important;
          }
          .overdue-table-scroll {
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          .overdue-table-scroll .rdt_Table {
            min-width: 1100px;
          }
          .overdue-table-scroll .rdt_TableCol {
            justify-content: center !important;
            text-align: center !important;
          }
          .overdue-table-scroll .rdt_TableCell {
            justify-content: flex-end !important;
            text-align: right !important;
          }
          .overdue-table-scroll .rdt_TableCol > div {
            justify-content: center !important;
            width: 100%;
          }
          .overdue-table-scroll .rdt_TableHead .rdt_TableCol[data-column-id="12"],
          .overdue-table-scroll .rdt_TableBody .rdt_TableCell[data-column-id="12"] {
            position: sticky !important;
            right: 48px !important;
            z-index: 2 !important;
          }
          .overdue-table-scroll .rdt_TableHead .rdt_TableCol[data-column-id="13"],
          .overdue-table-scroll .rdt_TableBody .rdt_TableCell[data-column-id="13"] {
            position: sticky !important;
            right: 0 !important;
            z-index: 2 !important;
          }
          .overdue-table-scroll .rdt_TableBody .rdt_TableCell[data-column-id="12"],
          .overdue-table-scroll .rdt_TableBody .rdt_TableCell[data-column-id="13"] {
            background: #FFFFFF !important;
          }
          .overdue-totals-card {
            flex: 1 1 280px;
            min-width: min(100%, 280px);
            max-width: 100%;
          }
          .overdue-constrained {
            width: 100%;
            max-width: 75%;
          }
          .overdue-filters {
            width: 100%;
          }
          /* Outer control only — not DatePicker days / Dropdown options (also buttons). */
          .overdue-filter-active:not(.date-filter-input) > div > div:first-of-type:not(:has(button)) {
            box-shadow: inset 0 0 0 1px ${activeFilterBorder} !important;
          }
          .overdue-filter-active > div > div > button:first-of-type,
          .overdue-filter-active > div > div > div > button:first-of-type {
            box-shadow: inset 0 0 0 1px ${activeFilterBorder} !important;
          }
          .overdue-filter-active.date-filter-input div:has(> input) {
            box-shadow: inset 0 0 0 1px ${activeFilterBorder} !important;
          }
          .overdue-filter-actions {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-shrink: 0;
            height: 52px;
            align-self: flex-end;
          }
          .overdue-filter-actions button {
            background: none;
            border: none;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
            cursor: pointer;
          }
          .overdue-filter-actions button:disabled {
            cursor: not-allowed;
          }
          .overdue-header-actions {
            flex-wrap: wrap;
          }
          @media (max-width: 768px) {
            .overdue-constrained {
              max-width: 100%;
            }
            .overdue-header-row > div {
              flex-direction: column !important;
              align-items: flex-start !important;
              gap: 12px !important;
            }
          }
        `}</style>

        <div className='overdue-header-row' style={{ width: '100%' }}>
          <Card display='flex' justifyContent='space-between' alignItems='center' gap='12px' flexWrap='wrap'>
            <H withMargin={false} type='1' weight='bold' modifier='strong'>
              Vencimientos
            </H>
            <div className='overdue-header-actions'>
              <Card display='flex' gap='12px' flexWrap='wrap'>
                <Button variant='secondary' onClick={handleTriggerOverdue} disabled={isTriggeringOverdue}>
                  {isTriggeringOverdue ? 'Procesando...' : 'Actualizar Vencimientos'}
                </Button>
                <Button variant='secondary' onClick={handleTriggerCollectedOk} disabled={isTriggeringCollectedOk}>
                  {isTriggeringCollectedOk ? 'Procesando...' : 'Actualizar Cobradas'}
                </Button>
              </Card>
            </div>
          </Card>
        </div>

        {totals && (
          <div className='overdue-constrained'>
            <Card display='flex' gap='16px' margin={{ top: '3' }} flexWrap='wrap'>
              <div className='overdue-totals-card'>
                <Card
                  variant='neutral'
                  style={{
                    backgroundColor: '#F3F4F6',
                    borderRadius: '8px',
                    border: 'none',
                    padding: '20px 24px',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <P type='3' withMargin={false} style={{ color: '#4B5563', fontSize: '14px' }}>
                    Mes actual
                  </P>
                  <P type='1' weight='bold' withMargin={false} style={{ fontSize: '28px', color: '#374151', marginTop: '12px' }}>
                    {formatCurrency(totals.current_month.total_ars, localCurrencyCode)}
                  </P>
                </Card>
              </div>
              <div className='overdue-totals-card'>
                <Card
                  variant='neutral'
                  style={{
                    backgroundColor: '#F3F4F6',
                    borderRadius: '8px',
                    border: 'none',
                    padding: '20px 24px',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <P type='3' withMargin={false} style={{ color: '#4B5563', fontSize: '14px' }}>
                    Mes próximo
                  </P>
                  <P type='1' weight='bold' withMargin={false} style={{ fontSize: '28px', color: '#374151', marginTop: '12px' }}>
                    {formatCurrency(totals.next_month.total_ars, localCurrencyCode)}
                  </P>
                </Card>
              </div>
            </Card>
          </div>
        )}

        <div className='overdue-filters'>
          <Card display='flex' margin={{ top: '4', bottom: '3' }} gap='12px' alignItems='end' flexWrap='wrap'>
            <div
              className={isGlobalIdFilterActive ? 'overdue-filter-active' : undefined}
              style={{ flex: '1 1 110px', minWidth: '100px', maxWidth: '100%' }}
            >
              <Input
                type='text'
                size='md'
                icons={{ iconLeft: 'search_outline' }}
                placeholder='Buscar N° aval'
                value={globalIdContains}
                onChange={(e) => setGlobalIdContains(e.target.value.replace(/\D/g, ''))}
              />
            </div>

            <div
              className={isSearchFilterActive ? 'overdue-filter-active' : undefined}
              style={{ flex: '1 1 180px', minWidth: '150px', maxWidth: '100%' }}
            >
              <Input
                type='text'
                size='md'
                icons={{ iconLeft: 'search_outline' }}
                placeholder={fiscalId.searchPlaceholder}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>

            <div
              className={`date-filter-input${isFromDateActive ? ' overdue-filter-active' : ''}`}
              style={{ flex: '1 1 110px', minWidth: '100px', maxWidth: '100%' }}
            >
              <DatePicker
                placeholder='Desde'
                displayFormat='dd-MM-yyyy'
                defaultValue={fromDate}
                onChange={(date: Date | string | null) => handleFromDateChange(parseDatePickerValue(date))}
                disabled={false}
              />
            </div>

            <div
              className={`date-filter-input${isToDateActive ? ' overdue-filter-active' : ''}`}
              style={{ flex: '1 1 110px', minWidth: '100px', maxWidth: '100%' }}
            >
              <DatePicker
                placeholder='Hasta'
                displayFormat='dd-MM-yyyy'
                defaultValue={toDate}
                onChange={(date: Date | string | null) => handleToDateChange(parseDatePickerValue(date))}
                disabled={false}
              />
            </div>

            <div
              className={isBadgeFilterActive ? 'overdue-filter-active' : undefined}
              style={{ flex: '1 1 150px', minWidth: '135px', maxWidth: '100%' }}
            >
              <Dropdown
                id='badge-filter'
                title={badgeFilterOptions.find((o) => o.value === badgeFilter)?.label || 'Todos los estados'}
                items={badgeFilterOptions.map((o) => ({ label: o.label }))}
                onChange={handleBadgeFilterChange}
                size='md'
              />
            </div>

            <div
              className={isCreditorFilterActive ? 'overdue-filter-active' : undefined}
              style={{ flex: '1 1 150px', minWidth: '135px', maxWidth: '100%' }}
            >
              <Dropdown
                id='creditor-filter'
                title={
                  filters.creditors.find((c) => String(c.id) === selectedCreditor)?.name ||
                  'Todos los acreedores'
                }
                items={[
                  { label: 'Todos los acreedores' },
                  ...filters.creditors.map((c) => ({ label: c.name })),
                ]}
                onChange={handleCreditorChange}
                size='md'
              />
            </div>

            <div
              className={isTypeFilterActive ? 'overdue-filter-active' : undefined}
              style={{ flex: '1 1 170px', minWidth: '150px', maxWidth: '100%' }}
            >
              <Dropdown
                id='type-filter'
                title={
                  filters.types.find((t) => String(t.id) === selectedType)?.name ||
                  'Todos los instrumentos'
                }
                items={[
                  { label: 'Todos los instrumentos' },
                  ...filters.types.map((t) => ({ label: t.name })),
                ]}
                onChange={handleTypeChange}
                size='md'
              />
            </div>

            <div className='overdue-filter-actions'>
              <Tooltip position='top' msg='Limpiar filtros'>
                <button
                  type='button'
                  aria-label='Limpiar filtros'
                  onClick={handleClearFilters}
                >
                  <Icon
                    name='delete_outline'
                    variant={isClearFiltersDirty ? 'danger' : 'neutral'}
                    width={24}
                    height={24}
                    cursor='pointer'
                  />
                </button>
              </Tooltip>
              <Tooltip position='top' msg='Descargar resultados'>
                <button
                  type='button'
                  aria-label='Descargar resultados'
                  onClick={handleExportCsv}
                  disabled={isExporting}
                >
                  {isExporting ? (
                    <Spinner size='sm' variant='primary' />
                  ) : (
                    <Icon name='download_outline' width={24} height={24} cursor='pointer' variant='info' />
                  )}
                </button>
              </Tooltip>
            </div>
          </Card>
          {dateError && (
            <P type='3' variant='danger' withMargin={false} style={{ marginTop: '4px' }}>
              {dateError}
            </P>
          )}
        </div>

        {isLoading && overdueData.length === 0 ? (
          <Card margin={{ top: '4' }}>
            <TableSkeleton rows={10} columns={columns.length} />
          </Card>
        ) : overdueData.length > 0 ? (
          <Card
            margin={{ top: '4' }}
            width='100%'
            className='overdue-table-scroll'
            style={{ opacity: isLoading ? 0.55 : 1, transition: 'opacity 120ms ease' }}
          >
            <Table
              columns={columns}
              data={overdueData}
              tableData={tableData}
              handleChangePage={(e: number) => handleChangePage(e)}
              onSort={handleSort}
              hideNativeSortIcon
              keyField='row_key'
            />
          </Card>
        ) : (
          <Card
            display='grid'
            justifyContent='center'
            alignItems='center'
            margin={{ top: '11', bottom: '11' }}
            width='100%'
          >
            <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
              <Image name='folder_empty' />
            </Card>
            <P variant='primary' align='center' withMargin={false} weight='bold'>
              No encontramos resultados.
            </P>
            <P variant='primary' align='center'>
              Podés hacer una nueva búsqueda.
            </P>
          </Card>
        )}

        <OverdueStatusModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          overdueItem={selectedOverdueItem}
          onSuccess={handleModalSuccess}
        />

        <Modal
          isOpen={showResultModal}
          onClose={handleCloseResultModal}
          title={resultModalTitle}
          primaryText='Aceptar'
          primaryVariant='primary'
          onPrimaryAction={handleCloseResultModal}
        >
          <Card>
            <P type='2' variant='neutral' modifier='regular'>
              {resultModalMessage}
            </P>
          </Card>
        </Modal>
      </Card>
    </>
  );
};

export default Overdue;
